export const WORKSPACE_LIFECYCLES = ['active', 'archiving', 'archived'] as const;
export type WorkspaceLifecycle = (typeof WORKSPACE_LIFECYCLES)[number];

// Caller-facing workspace reference: a live display name, or the canonical id
// for internal callers holding a projection (workspace_state ADR-6adfe).
// Resolution is server-side and tenant-scoped; a name that matches zero or
// multiple live workspaces surfaces the backend 404/409 — never a guess.
export type WorkspaceRef = string | { name: string };

// Name-catalog row: every non-deleted workspace, equipment-free
// (workspace_state ADR-6adfe)
export interface WorkspaceNameEntry {
  workspace_id: string;
  name: string;
  lifecycle: WorkspaceLifecycle;
}

export const WORKSPACE_ROUTING_CLASSES = ['local', 'cloud'] as const;
export type WorkspaceRoutingClass = (typeof WORKSPACE_ROUTING_CLASSES)[number];

export const WORKSPACE_FILESYSTEM_KINDS = ['local', 'cloud'] as const;
export type WorkspaceFilesystemKind = (typeof WORKSPACE_FILESYSTEM_KINDS)[number];

export const WORKSPACE_FILESYSTEM_STATES = ['provisioning', 'ready', 'error', 'retired'] as const;
export type WorkspaceFilesystemState = (typeof WORKSPACE_FILESYSTEM_STATES)[number];

export const WORKSPACE_WORKSTATION_KINDS = ['local', 'cloud'] as const;
export type WorkspaceWorkstationKind = (typeof WORKSPACE_WORKSTATION_KINDS)[number];

export const WORKSPACE_WORKSTATION_STATUSES = [
  'pending',
  'connected',
  'disconnected',
  'starting',
  'running',
  'sleeping',
  'reclaiming',
  'error',
  'failed',
  'retired',
] as const;
export type WorkspaceWorkstationStatus = (typeof WORKSPACE_WORKSTATION_STATUSES)[number];

export const WORKSPACE_INDEX_PROGRESS = ['idle', 'indexing'] as const;
export type WorkspaceIndexProgress = (typeof WORKSPACE_INDEX_PROGRESS)[number];

export const WORKSPACE_INDEX_HEALTH = ['ok', 'degraded'] as const;
export type WorkspaceIndexHealth = (typeof WORKSPACE_INDEX_HEALTH)[number];

export const WORKSPACE_ACTIONS = [
  'save',
  'list_history',
  'restore',
  'environment_save',
  'bind',
  'unbind',
  'transfer',
  'convert',
  'archive',
  'wake',
  'sleep',
  'cancel_operation',
  'retry_cleanup',
  'reassign_workstation',
] as const;
export type WorkspaceAction = (typeof WORKSPACE_ACTIONS)[number];

export const WORKSPACE_REFUSAL_REASONS = [
  'workspace_retired',
  'workspace_archiving',
  'operation_in_progress',
  'stale_operation',
  'filesystem_not_ready',
  'workstation_disconnected',
  'workstation_failed',
  'folder_unbound',
  'folder_missing',
  'divergence_changed',
  'cloud_disabled',
  'workspace_not_cloud',
  'workstation_already_running',
  'workstation_already_sleeping',
  'workstation_not_wakeable',
  'workstation_not_sleepable',
  'cancellation_not_supported',
  'operation_not_running',
  'workspace_already_cloud',
  'workstation_not_ready',
  'environment_images_removed',
  'workspace_not_local',
] as const;
export type WorkspaceRefusalReason = (typeof WORKSPACE_REFUSAL_REASONS)[number];

export const WORKSPACE_OPERATION_KINDS = [
  'create_local',
  'create_cloud',
  'bind',
  'unbind',
  'transfer',
  'convert',
  'archive',
  'environment_save',
] as const;
export type WorkspaceOperationKind = (typeof WORKSPACE_OPERATION_KINDS)[number];

export const WORKSPACE_OPERATION_STATES = ['running', 'succeeded', 'failed', 'canceled'] as const;
export type WorkspaceOperationState = (typeof WORKSPACE_OPERATION_STATES)[number];

export const WORKSPACE_BINDING_STATES = ['bound', 'missing', 'unbound'] as const;
export type WorkspaceBindingState = (typeof WORKSPACE_BINDING_STATES)[number];

export interface WorkspaceAssignment {
  routing_class: WorkspaceRoutingClass;
  filesystem_id: string;
  workstation_id: string;
  epoch: number;
}

export interface WorkspaceIndexState {
  progress: WorkspaceIndexProgress;
  health: WorkspaceIndexHealth;
  emitter_epoch: number;
  index_revision: number;
  queued: number;
  embedding: number;
  indexed: number;
}

export interface WorkspaceFilesystem {
  id: string;
  kind: WorkspaceFilesystemKind;
  state: WorkspaceFilesystemState;
  failure_reason?: string;
}

export interface WorkspaceWorkstation {
  id: string;
  kind: WorkspaceWorkstationKind;
  status: WorkspaceWorkstationStatus;
  device_id?: string;
  wakeable: boolean;
  environment_unsaved: boolean;
  /** The workstation's environment-data snapshot, recorded on destroy and restored on wake. */
  environment_snapshot_state?: WorkspaceEnvironmentSnapshotState;
  failure_reason?: string;
}

export interface WorkspaceEnvironmentPackage {
  manager: 'apk' | 'apt' | 'npm' | 'pip';
  name: string;
  version: string;
}

export interface WorkspaceEnvironmentDeclaration {
  image: string;
  features?: string[];
  packages?: WorkspaceEnvironmentPackage[];
  containerEnv?: Record<string, string>;
  postCreateCommand?: string[];
}

export type WorkspaceEnvironmentSnapshotState =
  | 'pending'
  | 'ready'
  | 'missing'
  | 'unsupported'
  | 'corrupt'
  | 'failed';

export interface SaveWorkspaceEnvironmentRequest {
  idempotency_key: string;
  declaration: WorkspaceEnvironmentDeclaration;
}

export interface WorkspaceCapabilities {
  content_readable: boolean;
  content_writable: boolean;
  execution: boolean;
  search_current: boolean;
  version_history: boolean;
}

export interface WorkspaceActionRefusal {
  reason: WorkspaceRefusalReason;
  message: string;
  operation_id?: string;
  operation_kind?: WorkspaceOperationKind;
}

export type WorkspaceActionAvailability =
  | { available: true; refusal?: never }
  | { available: false; refusal: WorkspaceActionRefusal };

export interface WorkspaceOperation {
  id: string;
  kind: WorkspaceOperationKind;
  state: WorkspaceOperationState;
  accepted_revision: number;
  operation_epoch: number;
  reason?: string;
  message?: string;
  proof?: WorkspaceTransferOutcome | Record<string, unknown>;
  divergence_resolution?: {
    push: DivergenceResolutionOption;
    pull: DivergenceResolutionOption;
  };
}

export interface DivergenceResolutionOption {
  expected_source: ContentIdentityAnchor;
  expected_destination: ContentIdentityAnchor;
}

export interface WorkspaceTransferOutcome {
  status: 'succeeded' | 'needs_resolution' | 'failed';
  direction?: 'push' | 'pull';
  proof?: Record<string, unknown>;
  source?: ContentIdentity;
  destination?: ContentIdentity;
}

export interface WorkspaceOperationHistoryEntry extends WorkspaceOperation {
  idempotency_key: string;
  proof?: Record<string, unknown>;
  terminal_at?: string;
  created_at: string;
  updated_at: string;
}

export interface WorkspaceTransition {
  id: string;
  revision: number;
  operation_id?: string;
  transition_type: string;
  actor_type: string;
  actor_id?: string;
  facts: Record<string, unknown>;
  created_at: string;
}

export interface WorkspaceOperationHistory {
  workspace_id: string;
  operations: WorkspaceOperationHistoryEntry[];
  transitions: WorkspaceTransition[];
}

export interface WorkspaceBinding {
  device_id: string;
  folder_path: string;
  state: WorkspaceBindingState;
  connected: boolean;
  last_outcome?: WorkspaceTransferOutcome;
  divergence_resolution?: {
    push: DivergenceResolutionOption;
    pull: DivergenceResolutionOption;
  };
}

export interface WorkspaceProjection {
  workspace_id: string;
  name: string;
  // Absent on the wire from N-1 deployables (la ADR-5ced9)
  workspace_root?: string;
  revision: number;
  operation_epoch: number;
  lifecycle: WorkspaceLifecycle;
  assignment: WorkspaceAssignment;
  filesystem: WorkspaceFilesystem;
  workstation: WorkspaceWorkstation;
  index: WorkspaceIndexState;
  capabilities: WorkspaceCapabilities;
  actions: Record<WorkspaceAction, WorkspaceActionAvailability>;
  operation: WorkspaceOperation | null;
  binding: WorkspaceBinding | null;
  created_at: string;
  updated_at: string;
}

export interface CreateLocalWorkspaceRequest {
  name?: string;
  workspace_root: string;
  workspace_settings_dir?: string;
  device_id: string;
  idempotency_key: string;
}

export interface SubscribeWorkspaceEventsRequest {
  device_id: string;
  after_revision: number;
}

interface CreateCloudWorkspaceRequestBase {
  kind: 'cloud';
  name?: string;
  idempotency_key: string;
}

export type CreateCloudWorkspaceRequest = CreateCloudWorkspaceRequestBase &
  (
    | { source_device_id: string; source_root: string }
    | { source_device_id?: never; source_root?: never }
  );

export interface BindWorkspaceRequest {
  device_id: string;
  folder_path: string;
  idempotency_key: string;
}

export interface ReassignWorkstationRequest {
  device_id: string;
  folder_path: string;
  workspace_settings_dir?: string;
  idempotency_key: string;
}

export interface UnbindWorkspaceRequest {
  idempotency_key: string;
}

export interface ConvertWorkspaceRequest {
  idempotency_key: string;
}

export interface ArchiveWorkspaceRequest {
  idempotency_key: string;
  reason?: string;
}

export interface WorkspaceLifecycleActionRequest {
  idempotency_key: string;
}

export interface RetryWorkspaceArchiveCleanupRequest {
  operation_id: string;
}

export interface CancelWorkspaceOperationRequest {
  idempotency_key: string;
}

export interface ContentIdentityAnchor {
  tree: string;
  sidecar: string;
}

export interface ContentIdentity extends ContentIdentityAnchor {
  empty_directory_count?: number;
  file_count?: number;
  total_bytes?: number;
}

export interface TransferWorkspaceRequest {
  device_id: string;
  direction: 'push' | 'pull';
  expected_source?: ContentIdentityAnchor;
  expected_destination?: ContentIdentityAnchor;
  resolve_divergence?: boolean;
  idempotency_key: string;
}
